/**
 * Η απόφαση τιμής ενός κεντρικού είδους (§13.19.Β).
 *
 * ΓΙΑΤΙ ξεχωριστό, μικρό component: το ΙΔΙΟ χειριστήριο εμφανίζεται σε δύο
 * σημεία — στην υπο-καρτέλα «Τιμές» και μέσα στο παράθυρο του συγχρονισμού. Αν
 * το γράφαμε δύο φορές, η μία από τις δύο θα ξεχνούσε τον κανόνα «σταθερή τιμή
 * ΧΩΡΙΣ ποσό = μη αποθηκεύσιμη γραμμή» (το CHECK `pcpo_fixed_chk` του 228 είναι
 * αμφίδρομο) και ο χειριστής θα έπαιρνε 500 αντί για εξήγηση.
 *
 * Η απόφαση είναι ΜΟΝΙΜΗ και κλειδί της είναι το `master_key`: τα `item_code`
 * αλλάζουν ΟΛΑ στη μετάπτωση (§13.19.Ε) — το κεντρικό είδος επιβιώνει.
 */

import React from 'react';
import { OverrideMode, PriceOverride } from '../../services/platformCatalogApi';
import { fmtMoney, OVERRIDE_MODE_HINTS, OVERRIDE_MODE_LABELS, parsePriceInput } from './catalogUi';

interface Props {
  value: PriceOverride;
  /** Άλλαξε σε αυτή τη συνεδρία και δεν έχει αποθηκευτεί ακόμη. */
  dirty?: boolean;
  disabled?: boolean;
  /** Η τιμή του καταλόγου — μπαίνει ως placeholder στη «σταθερή». */
  catalogPrice?: number | null;
  /** Η τιμή που έχει σήμερα το efood — η άλλη λογική επιλογή για «σταθερή». */
  platformPrice?: number | null;
  onChange: (next: PriceOverride) => void;
  /** Χωρίς το πεδίο σημείωσης (λίστες με πολλές γραμμές). */
  compact?: boolean;
  /**
   * Το είδος τιμολογείται ΜΕΣΩ ΕΠΙΛΟΓΩΝ ΜΕΓΕΘΟΥΣ. Τότε η «σταθερή τιμή» ΔΕΝ
   * προσφέρεται: το payload θα έγραφε το ποσό μόνο στο είδος, θα άφηνε κάθε
   * μέγεθος στην τιμή της πλατφόρμας και θα γύριζε το `ignore_base_price` σε
   * false — δηλαδή ούτε την παλιά τιμή ούτε αυτή που υποσχέθηκε η οθόνη. Το
   * `PUT …/price-overrides` το απορρίπτει ούτως ή άλλως με 400· η οθόνη δεν
   * ανοίγει ποτέ ό,τι ο server κλείνει.
   */
  sized?: boolean;
}

const MODES: OverrideMode[] = ['catalog', 'keep_platform', 'fixed'];
const SIZED_MODES: OverrideMode[] = ['catalog', 'keep_platform'];

const PriceDecision: React.FC<Props> = ({
  value, dirty = false, disabled = false, catalogPrice = null, platformPrice = null, onChange,
  compact = false, sized = false,
}) => {
  const mode: OverrideMode = value.mode || 'catalog';
  const fixedText = (value.fixed_price === null || value.fixed_price === undefined)
    ? ''
    : fmtMoney(value.fixed_price);
  const fixedMissing = mode === 'fixed' && !sized
    && (value.fixed_price === null || value.fixed_price === undefined);
  /**
   * Αποθηκευμένη «σταθερή» σε είδος με μεγέθη: ΔΕΝ την κρύβουμε από τον
   * επιλογέα (θα έδειχνε άλλη απόφαση από αυτή που είναι γραμμένη στη βάση) —
   * τη δείχνουμε ρητά ως αγνοημένη, ώστε να μπορεί να αλλαχθεί.
   */
  const ignoredFixed = sized && mode === 'fixed';
  const modes = sized ? (ignoredFixed ? MODES : SIZED_MODES) : MODES;

  const setMode = (next: OverrideMode) => {
    onChange({
      ...value,
      mode: next,
      // Η τιμή κρατιέται ΜΟΝΟ στη «σταθερή»: το CHECK του 228 απαγορεύει ποσό σε
      // οποιοδήποτε άλλο mode, και ένα ξεχασμένο ποσό θα γύριζε 23514/500.
      fixed_price: next === 'fixed'
        ? (value.fixed_price !== null && value.fixed_price !== undefined
          ? value.fixed_price
          : (platformPrice !== null ? platformPrice : catalogPrice))
        : null,
    });
  };

  return (
    <div className="flex flex-col gap-1 min-w-[190px]">
      <div className="flex items-center gap-1.5">
        <select
          value={mode}
          disabled={disabled}
          onChange={e => setMode(e.target.value as OverrideMode)}
          title={OVERRIDE_MODE_HINTS[mode]}
          className={`bg-surface-0 border rounded-lg px-2 py-1 text-xs text-t1 disabled:opacity-50 ${
            dirty ? 'border-blue-500' : 'border-line-strong'}`}
        >
          {modes.map(m => (
            <option key={m} value={m}>
              {m === 'fixed' && sized ? 'Σταθερή τιμή (αγνοείται — είδος με μεγέθη)' : OVERRIDE_MODE_LABELS[m]}
            </option>
          ))}
        </select>

        {mode === 'fixed' && !sized && (
          <input
            type="text"
            inputMode="decimal"
            disabled={disabled}
            value={fixedText}
            placeholder={catalogPrice !== null ? fmtMoney(catalogPrice) : 'π.χ. 3,40'}
            onChange={e => onChange({ ...value, mode: 'fixed', fixed_price: parsePriceInput(e.target.value) })}
            title="Η τιμή που θα σταλεί, ανεξάρτητα από κατάλογο και efood"
            className={`w-20 bg-surface-0 border rounded-lg px-2 py-1 text-xs text-right text-t1 disabled:opacity-50 ${
              fixedMissing ? 'border-red-500' : 'border-line-strong'}`}
          />
        )}

        {dirty && (
          <span className="text-[10px] px-1 rounded bg-blue-500/20 text-blue-700 dark:text-blue-300" title="Δεν έχει αποθηκευτεί">
            νέο
          </span>
        )}
      </div>

      {!compact && (
        <input
          type="text"
          disabled={disabled}
          value={value.note || ''}
          onChange={e => onChange({ ...value, note: e.target.value || null })}
          placeholder="Σημείωση (γιατί)"
          title="Γιατί πάρθηκε αυτή η απόφαση — το διαβάζει ο επόμενος που θα δει τη διαφορά"
          className="w-full bg-surface-0 border border-line-strong rounded-lg px-2 py-1 text-[11px] text-t2 disabled:opacity-50"
        />
      )}

      {fixedMissing && (
        <span className="text-[10px] text-red-600 dark:text-red-400">
          Η «σταθερή τιμή» χωρίς ποσό δεν αποθηκεύεται.
        </span>
      )}

      {ignoredFixed && (
        <span className="text-[10px] text-red-600 dark:text-red-400">
          Σταθερή τιμή δεν επιτρέπεται σε είδος με μεγέθη: η τιμή του είδους βγαίνει από τα μεγέθη.
          Διάλεξε «κατάλογος» ή «κράτα την τιμή efood».
        </span>
      )}

      {sized && !ignoredFixed && (
        <span className="text-[10px] text-t4">
          Είδος με μεγέθη: η τιμή κάθε μεγέθους είναι απόλυτη και η τιμή του είδους είναι το φθηνότερο.
        </span>
      )}
    </div>
  );
};

export default PriceDecision;
